import { connect } from 'react-redux';
import { push } from 'redux-first-history';

import { logError } from '@zg-rentals/log-error';
import { formatYMDStringToTimestamp } from '@common/paymentsUtils/dateUtils';

import ManagePaymentMethodsPage from './component';
import paths from '@renter-hub/pathsManifest';
import { closeNotification, showModalNotification, showPageNotification } from '@common/actions/NotificationActions';
import { getRachelDashboard, requestUpdateModeLinkToken } from '@renter-hub/actions/requests';
import type { CreatePaymentSubscriptionWebApiRequestDto } from '@zg-rentals/ts-rent-payment-client';
import {
  CreatePaymentSubscriptionWebApiHandler_createPaymentSubscription,
  ScheduleMoveInCostsWebApiHandler_scheduleMoveInCosts,
} from '@zg-rentals/ts-rent-payment-client';
import { checkRequestActionSuccess, getSignInPage } from '@common/paymentsUtils/api';
import TransactionConstants from '@common/constants/TransactionConstants';
import {
  DEFAULT_ERROR,
  PAYMENT_METHOD_DELETED_SUCCESS,
  PAYMENT_METHOD_INSUFFICIENT_FUNDS,
  PAYMENT_METHOD_UNPROCESSABLE,
  PAYMENT_SCHEDULED_SUCCESS,
  PAYMENT_TIMEOUT,
  PAYMENT_UPDATED_SUCCESS,
  PLAID_CREATE_PAYMENT_METHOD_ERROR,
} from '@renter-hub/constants/notifications';
import type { ErrorInterface } from '@common/types';
import HTTP_STATUS from '@common/constants/HttpConstants';
import ResponseMessageCode from '@common/constants/ResponseMessageCode';
import { REQUEST_TIMEOUT } from '../../../utils/customErrorTypes';
import { getPaymentErrorInfo } from '../../../utils/getPaymentErrorInfo';
import { trackValidationError } from '@common/actions/TrackingActions';

const mapStateToProps = (state) => ({
  addressInfo: state.payments.renter.dashboard.rentTerms.addressInfo,
  paymentMethods: state.payments.renter.dashboard.paymentMethods,
  paymentSubscriptionDetails: state.payments.renter.dashboard.paymentDetails.paymentSubscriptionDetails,
  moveInCostsDetails: state.payments.renter.dashboard.moveInCostsDetails,
  nextPayable: state.payments.renter.dashboard.nextPayable,
  nextScheduleMonth: state.payments.renter.dashboard.nextScheduleMonth,
  paymentMethodTypes: state.payments.renter.dashboard.paymentMethodTypes,
  transactions: state.payments.renter.dashboard.paymentHistory.transactions,
  currentScheduleMonth: state.payments.renter.dashboard.currentScheduleMonth,
  config: state.payments.renter.config,
});

const mapDispatchToProps = (dispatch, { baseUrl, location, brand, propertyId }) => ({
  onCancel: () => {
    // redirect to either the previous page, or the dashboard (default)
    const fromWhere = location?.state?.fromWhere;
    const toUrl = fromWhere === paths.paymentSettingsPage ? baseUrl + paths.paymentSettingsPage : baseUrl;

    return dispatch(push(toUrl));
  },
  onAddNewPaymentClick: () => {
    return dispatch(push(baseUrl + paths.editPaymentInfoPage));
  },
  onDeletePaymentMethodClickSuccess: async (paymentMethodIdToDelete, paymentMethods) => {
    try {
      await dispatch(getRachelDashboard({ propertyId }));
      await dispatch(push(baseUrl));
      const paymentMethodInfo = paymentMethods.find((paymentMethod) => paymentMethod.id === paymentMethodIdToDelete);
      await dispatch(
        showPageNotification(PAYMENT_METHOD_DELETED_SUCCESS, { paymentMethodDeleted: paymentMethodInfo?.displayName }),
      );
    } catch (error) {
      logError({ error });
      await dispatch(showModalNotification(DEFAULT_ERROR));
      return { error: true };
    }
  },
  getPlaidUpdateModeLinkToken: (externalId) =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(requestUpdateModeLinkToken(externalId)))
      .catch((err) => {
        logError({ error: err });
        return dispatch(showPageNotification(DEFAULT_ERROR, { brand }));
      }),
  onPaymentMethodCreationCompletion: async ({ errorMessage }: { errorMessage?: string }) => {
    try {
      if (errorMessage) {
        throw new Error(errorMessage);
      }

      const [renterStatusResponse] = await dispatch(getRachelDashboard({ propertyId }));
      await checkRequestActionSuccess(renterStatusResponse);

      await dispatch(push(baseUrl));
      await dispatch(showPageNotification(PAYMENT_UPDATED_SUCCESS));

      return { error: false };
    } catch (err) {
      // By default, show the default error banner
      let notificationType = DEFAULT_ERROR;
      const error = err as ErrorInterface;
      const message = err.message ?? '';

      if (error.action && error.action.payload) {
        const { httpStatus, status } = error.action.payload;
        const resolvedStatus = httpStatus ?? status;

        // If 401 status, redirect to sign-in
        if (resolvedStatus === HTTP_STATUS.SC_UNAUTHORIZED) {
          window.location = getSignInPage();
          return null;
        }

        // If 422 status, show payment method unprocessable banner
        if (resolvedStatus === HTTP_STATUS.SC_UNPROCESSABLE_ENTITY) {
          notificationType = PAYMENT_METHOD_UNPROCESSABLE;
        }
      }

      logError({ error: err });
      if (message) {
        await dispatch(showModalNotification(PLAID_CREATE_PAYMENT_METHOD_ERROR, { message }));
      } else {
        await dispatch(showModalNotification(notificationType));
      }
      return { error: true };
    }
  },
  onSubmitDeletePaymentMethod: async ({
    moveInCostsPaymentMethodId,
    monthlyRentPaymentMethodId,
    paymentMethodIdToDelete,
    paymentSubscriptionDetails,
    moveInCostsDetails,
    nextPayable,
    nextScheduleMonth,
    transactions,
    currentScheduleMonth,
    deletePaymentMethodFn,
  }) => {
    try {
      await dispatch(closeNotification());

      const currentTransaction = transactions.find(
        (transaction) => currentScheduleMonth.scheduleMonth === transaction.scheduleMonth,
      );

      const isTransactionPaid = currentTransaction?.renterStatus === TransactionConstants.STATUS.COMPLETE;
      if (paymentSubscriptionDetails?.paymentMethodId) {
        const createPaymentPayload: CreatePaymentSubscriptionWebApiRequestDto = {
          scheduleMonth: isTransactionPaid ? nextScheduleMonth.scheduleMonth : nextPayable.scheduleMonth,
          paymentDate: formatYMDStringToTimestamp(paymentSubscriptionDetails.nextScheduledDate),
          encodedPaymentMethodId: monthlyRentPaymentMethodId,
          recurring: paymentSubscriptionDetails.recurring,
        };
        await CreatePaymentSubscriptionWebApiHandler_createPaymentSubscription(createPaymentPayload);
      }

      if (moveInCostsDetails?.paymentMethodId) {
        const [year, month, day] = moveInCostsDetails.sendDate.split('-').map(Number);

        await ScheduleMoveInCostsWebApiHandler_scheduleMoveInCosts({
          encodedPaymentMethodId: moveInCostsPaymentMethodId,
          groupKey: moveInCostsDetails.groupKey,
          paymentDate: { day, month, year },
        });
      }

      // Delete the old payment method after rescheduling succeeds
      await deletePaymentMethodFn({ postData: { encodedPaymentMethodId: paymentMethodIdToDelete } });

      const [renterStatusResponse] = await dispatch(getRachelDashboard({ propertyId }));
      await checkRequestActionSuccess(renterStatusResponse);

      await dispatch(push(baseUrl));

      await dispatch(showPageNotification(PAYMENT_SCHEDULED_SUCCESS));

      return { error: false };
    } catch (err) {
      const { httpStatus, errorCode } = getPaymentErrorInfo(err);

      // By default, show the default error banner
      let notificationType = DEFAULT_ERROR;

      // If 401 status, redirect to sign-in
      if (httpStatus === HTTP_STATUS.SC_UNAUTHORIZED) {
        window.location = getSignInPage();
        return null;
      }

      // If 408 status, show timeout message and redirect to dashboard
      if (httpStatus === HTTP_STATUS.SC_REQUEST_TIMEOUT) {
        logError({ error: err, errorType: REQUEST_TIMEOUT, context: { status: httpStatus, messageCode: errorCode } });

        await dispatch(push(baseUrl));
        notificationType = PAYMENT_TIMEOUT;
      }

      // If 422, show insufficient funds if message code is insufficient_funds
      if (httpStatus === HTTP_STATUS.SC_UNPROCESSABLE_ENTITY && errorCode === ResponseMessageCode.INSUFFICIENT_FUNDS) {
        notificationType = PAYMENT_METHOD_INSUFFICIENT_FUNDS;
      }

      logError({ error: err, context: { notificationType } });
      dispatch(showModalNotification(notificationType));
      return { error: true };
    }
  },
  trackValidationError: (data) => dispatch(trackValidationError(data)),
});

const mergeProps = (stateProps, dispatchProps) => {
  return {
    ...stateProps,
    ...dispatchProps,
    onSubmitDeletePaymentMethod: ({
      moveInCostsPaymentMethodId,
      monthlyRentPaymentMethodId,
      paymentMethodIdToDelete,
      deletePaymentMethodFn,
    }) =>
      dispatchProps.onSubmitDeletePaymentMethod({
        moveInCostsPaymentMethodId,
        monthlyRentPaymentMethodId,
        paymentMethodIdToDelete,
        paymentSubscriptionDetails: stateProps.paymentSubscriptionDetails,
        nextPayable: stateProps.nextPayable,
        moveInCostsDetails: stateProps.moveInCostsDetails,
        nextScheduleMonth: stateProps.nextScheduleMonth,
        transactions: stateProps.transactions,
        currentScheduleMonth: stateProps.currentScheduleMonth,
        deletePaymentMethodFn,
      }),
    onDeletePaymentMethodClick: (paymentMethodIdToDelete) =>
      dispatchProps.onDeletePaymentMethodClick(paymentMethodIdToDelete, stateProps.paymentMethods),
  };
};
export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(ManagePaymentMethodsPage);
